import axios from 'axios'
import { ref, type Ref } from 'vue'
import type { APIResponse } from '@/types/Response'
import type {
    PlatformRedirect,
    PlatformSubscriptionSummary,
    RegistrationOptions
} from '@/types/PlatformBilling'

/**
 * The platform billing endpoints: what a gym pays Manajits.
 *
 * Separate from `billing.ts`, which is how a gym bills its members. The
 * `/gym/subscription` routes are `admin` or `manager` only and scoped to the
 * caller's own gym, so there is no tenant id to pass. Students and
 * instructors get a 403, so nothing may call these for them.
 *
 * Each function returns the unwrapped payload, as in `billing.ts`.
 */

/** The query key for the subscription summary. Keyed by gym, so a new sign-in starts clean. */
export function platformSubscriptionKey(gymId: string) {
    return ['platform_subscription', gymId]
}

/** The whole platform billing state for the caller's gym. */
export async function fetchPlatformSubscription({
    signal
}: {
    signal?: AbortSignal
} = {}): Promise<PlatformSubscriptionSummary> {
    const response = await axios.get<APIResponse<PlatformSubscriptionSummary>>(
        '/gym/subscription',
        { signal }
    )
    return response.data.data
}

/**
 * Starts Stripe Checkout for a plan.
 *
 * Call this on click and redirect at once. The session expires, so never
 * store the URL or show it as a link.
 *
 * 409 when the gym already has a subscription (change plan instead), 422 when
 * the plan is too small for the gym's students, 404 when the plan is not
 * available to this gym.
 */
export async function startPlatformCheckout(planId: string): Promise<PlatformRedirect> {
    const response = await axios.post<APIResponse<PlatformRedirect>>('/gym/subscription/checkout', {
        platform_plan_id: planId
    })
    return response.data.data
}

/**
 * Opens Stripe's billing portal, where the gym changes its card and reads its
 * invoices. Card details never touch this app.
 *
 * 409 when the gym has no Stripe customer yet, that is, before it subscribes.
 */
export async function openPlatformPortal(): Promise<PlatformRedirect> {
    const response = await axios.post<APIResponse<PlatformRedirect>>('/gym/subscription/portal')
    return response.data.data
}

/**
 * Asks the API to read the gym's subscription from Stripe now, rather than
 * wait for the webhook. Call it once when the browser returns from Checkout
 * or the portal. Returns the fresh summary.
 */
export async function refreshPlatformSubscription(): Promise<PlatformSubscriptionSummary> {
    const response = await axios.post<APIResponse<PlatformSubscriptionSummary>>(
        '/gym/subscription/refresh'
    )
    return response.data.data
}

/**
 * Changes the gym's plan.
 *
 * During a trial this only records the choice. On a live subscription it
 * changes the Stripe subscription, and Stripe prorates the next invoice.
 * Returns the fresh summary. 422 when the plan is too small.
 */
export async function changePlatformPlan(planId: string): Promise<PlatformSubscriptionSummary> {
    const response = await axios.put<APIResponse<PlatformSubscriptionSummary>>(
        '/gym/subscription/plan',
        { platform_plan_id: planId }
    )
    return response.data.data
}

/** What a closed or unreachable sign-up looks like. */
export const REGISTRATION_CLOSED: RegistrationOptions = Object.freeze({
    enabled: false,
    trial_days: null,
    plans: []
}) as RegistrationOptions

/** `GET /registration`. Public: no session needed. */
export async function fetchRegistrationOptions(): Promise<RegistrationOptions> {
    const response = await axios.get<APIResponse<RegistrationOptions>>('/registration')
    return response.data.data
}

let registrationRequest: Promise<RegistrationOptions> | null = null

/**
 * The sign-up options, fetched once per page load.
 *
 * The login page, the register page and the router guard all ask. One
 * request serves all three.
 *
 * Fails closed: an error reads as "sign-up is closed". A failure is not
 * cached, so the next caller tries again rather than staying closed until a
 * reload.
 */
export function loadRegistrationOptions(): Promise<RegistrationOptions> {
    if (!registrationRequest) {
        registrationRequest = fetchRegistrationOptions()
            .then((options) => ({
                enabled: options?.enabled === true,
                trial_days: options?.trial_days ?? null,
                plans: Array.isArray(options?.plans) ? options.plans : []
            }))
            .catch(() => {
                registrationRequest = null
                return REGISTRATION_CLOSED
            })
    }

    return registrationRequest
}

/** Clears the cache. For tests only. */
export function resetRegistrationOptionsCache() {
    registrationRequest = null
}

/**
 * The sign-up options as refs, for a component.
 *
 * `options` stays null while the request runs. A caller that shows a sign-up
 * link only when `options.enabled` is true never shows a link that then
 * disappears.
 */
export function useRegistrationOptions(): {
    options: Ref<RegistrationOptions | null>
    loading: Ref<boolean>
} {
    const options = ref<RegistrationOptions | null>(null)
    const loading = ref(true)

    loadRegistrationOptions().then((result) => {
        options.value = result
        loading.value = false
    })

    return { options, loading }
}

/**
 * Router guard for `/register`.
 *
 * Sends the visitor to sign-in when sign-up is closed, and also when the
 * check itself fails. Fail closed: the API refuses the POST anyway, and a
 * form that cannot be sent is worse than no form.
 */
export async function requireRegistrationOpen(): Promise<true | string> {
    const options = await loadRegistrationOptions()
    return options.enabled ? true : '/login'
}
